import { useState, useCallback } from 'react';
import { CustomTemplate, TemplateParameter, TemplateProcessingResponse } from '../types/messageForm.types';
import { apiService } from '../../../services/api';

export const useTemplateProcessing = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingError, setProcessingError] = useState<string | null>(null);

  /**
   * Validate parameters against template requirements
   */
  const validateParameters = useCallback(
    (template: CustomTemplate, values: Record<string, string>): { valid: boolean; errors: string[] } => {
      const errors: string[] = [];

      if (!template.hasParameters) {
        return { valid: true, errors: [] };
      }

      if (!template.parameters || template.parameters.length === 0) {
        return { valid: true, errors: [] };
      }

      // Check all required parameters are provided
      for (const param of template.parameters) {
        const key = `${param.section}-${param.position}`;
        if (param.isRequired && !(key in values)) {
          errors.push(`Missing required parameter at position ${param.position} (${param.section}): ${param.name || 'Value'}`);
        }

        // Validate pattern if provided
        if (param.pattern && key in values) {
          const regex = new RegExp(param.pattern);
          if (!regex.test(values[key])) {
            errors.push(`Parameter ${param.position} (${param.name || 'Value'}) does not match pattern: ${param.pattern}`);
          }
        }

        // Validate type if provided
        if (param.type && key in values) {
          const value = values[key];
          switch (param.type) {
            case 'url':
              if (!/^https?:\/\/.+/.test(value)) {
                errors.push(`Parameter ${param.position} must be a valid URL`);
              }
              break;
            case 'phone':
              if (!/^\+?[\d\s\-()]+$/.test(value)) {
                errors.push(`Parameter ${param.position} must be a valid phone number`);
              }
              break;
            case 'email':
              if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                errors.push(`Parameter ${param.position} must be a valid email`);
              }
              break;
            case 'number':
              if (!/^\d+(\.\d+)?$/.test(value)) {
                errors.push(`Parameter ${param.position} must be a number`);
              }
              break;
          }
        }
      }

      return {
        valid: errors.length === 0,
        errors,
      };
    },
    []
  );

  /**
   * Process template with parameters via API
   */
  const processTemplate = useCallback(
    async (
      templateId: string,
      parameters: Record<number, string>
    ): Promise<TemplateProcessingResponse | null> => {
      try {
        setIsProcessing(true);
        setProcessingError(null);

        const response = await apiService.processWhatsAppTemplate(templateId, parameters);

        if (response && response.data) {
          console.log('[useTemplateProcessing] Template processed successfully', { templateId });
          return response.data;
        }

        return null;
      } catch (err: any) {
        const errorMessage = err?.message || 'Failed to process template';
        console.error('[useTemplateProcessing] Error processing template:', err);
        setProcessingError(errorMessage);
        return null;
      } finally {
        setIsProcessing(false);
      }
    },
    []
  );

  /**
   * Get parameter input for UI display
   */
  const getParameterLabel = useCallback((param: TemplateParameter): string => {
    if (param.name) return param.name;
    return `Parameter ${param.position} (${param.section})`;
  }, []);

  /**
   * Generate placeholder text for parameter input
   */
  const getParameterPlaceholder = useCallback((param: TemplateParameter): string => {
    if (param.placeholder) return param.placeholder;

    switch (param.type) {
      case 'url':
        return 'https://example.com';
      case 'phone':
        return '+1234567890';
      case 'email':
        return 'example@domain.com';
      case 'number':
        return '0';
      default:
        return `Enter ${param.name || `parameter ${param.position}`}`;
    }
  }, []);

  return {
    isProcessing,
    processingError,
    validateParameters,
    processTemplate,
    getParameterLabel,
    getParameterPlaceholder,
  };
};
